import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';

import { Card } from '@/components/ui/Card';
import { Spinner } from '@/components/ui/Spinner';
import { Button } from '@/components/ui/Button';
import { chatApi } from '@/api/chat';
import { useActiveLease } from '@/hooks/useLeases';
import { useConversation, useMessages, useSendMessage } from '@/hooks/useChat';
import { useConversationChannel } from '@/hooks/useWebSocket';
import { useAuthStore } from '@/stores/authStore';

export function ChatPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { activeLease } = useActiveLease();
  const leaseId = activeLease?.id ?? null;
  const me = useAuthStore((s) => s.user);

  const conversation = useConversation(leaseId);
  const conversationId = conversation.data?.id ?? null;

  const messages = useMessages(conversationId);
  const send = useSendMessage(conversationId ?? 0);
  const [body, setBody] = useState('');
  const bottomRef = useRef<HTMLDivElement>(null);

  // Realtime: на '.new-message' просто перезагружаем список (payload без
  // подписанных URL вложений -- полную запись отдаёт GET messages).
  useConversationChannel(conversationId, () => {
    void messages.refetch();
  });

  // Отмечаем беседу прочитанной при открытии и после прихода новых сообщений.
  useEffect(() => {
    if (conversationId === null) return;
    void chatApi.markRead(conversationId).then(() => {
      void qc.invalidateQueries({ queryKey: ['chat', 'conversation', leaseId] });
    });
  }, [conversationId, leaseId, messages.data, qc]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.data]);

  if (!activeLease) {
    return (
      <Card title={t('chat.title')}>
        <p className="text-sm text-slate-600">{t('contacts.no_active_lease')}</p>
      </Card>
    );
  }

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!body.trim() || conversationId === null) return;
    await send.mutateAsync(body.trim());
    setBody('');
  };

  const loadingConversation = conversation.isLoading || conversationId === null;

  return (
    <Card title={t('chat.title')}>
      <div className="mb-3 flex h-96 flex-col gap-2 overflow-y-auto rounded-lg bg-slate-50 p-3">
        {loadingConversation || messages.isLoading ? <Spinner /> : null}
        {messages.data?.map((m) => {
          const mine = m.sender_user_id === me?.id;
          return (
            <div
              key={m.id}
              className={`max-w-[75%] rounded-lg px-3 py-2 text-sm ${
                mine ? 'ml-auto bg-brand text-white' : 'bg-white text-slate-900 shadow-sm'
              }`}
            >
              {m.content}
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>
      <form onSubmit={onSubmit} className="flex gap-2">
        <input
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder={t('chat.write_message')}
          className="flex-1 rounded-lg border border-slate-200 px-3 py-2 text-sm"
        />
        <Button
          type="submit"
          loading={send.isPending}
          disabled={!body.trim() || loadingConversation}
        >
          {t('chat.send')}
        </Button>
      </form>
    </Card>
  );
}
